import React, { useEffect, useState } from "react";
import { Button, Card, CardContent, Typography } from "@material-ui/core";
import { useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import Spinner from "../../shared/components/loader/Spinner";
import { DatabaseEntities } from "../../model/enum/database-entities.enum";
import ApiService from "../../service/ApiService";
import AlertService from "../../service/AlertService";
import EmailService from "../../service/EmailService";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Pathnames from "../../model/enum/Pathnames";
import ToastMessages from "../../model/enum/toast-messages.enum";
import User from "../../model/user";
import Logo from "../../assets/main.png";
import useStyles from "./styles";
import Misc from "../../utils/Misc";
import "./Login.scss";
import { PrimaryColor } from "../../shared/styles/colors";

const ActivationAccount = () => {
  const classes = useStyles();
  const [newPassword, setNewPassword] = useState("");
  const [confPassword, setConfPassword] = useState("");
  const [viewNewPassword, setViewNewPassword] = useState(false);
  const [viewConfPassword, setViewConfPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrortext] = useState("");
  const [toggleError, setToggleError] = useState(false);
  const [activationCode, setActivationCode] = useState("");
  const [toggleResend, setToggleResend] = useState(true);
  const location = useLocation();

  let queryParams = location.search.split("&");
  let userId = queryParams[0]?.split("=")[1]; // Get the userId from the query param
  let email = queryParams[1]?.split("=")[1]; // Get the email from the query param
  let activationToken = queryParams[2]?.split("=")[1]; // Get the activationToken from the query param

  useEffect(() => {
    setTimeout(() => {
      setToggleResend(true);
    }, 60000); // delay of 1 minute
  }, [toggleResend]);

  const invalidInput = (): boolean => {
    return (
      !Misc.validPasswordFormat(newPassword) ||
      !Misc.validPasswordFormat(confPassword) ||
      newPassword !== confPassword ||
      Misc.isNullOrEmpty(activationCode)
    );
  };

  const submit = async () => {
    if (invalidInput()) {
      setErrortext("Invalid password");
      setToggleError(true);
      return;
    }

    setToggleError(false);
    setLoading(true);

    const payload = {
      newPassword: newPassword,
      activationCode: activationCode,
      activationToken: activationToken,
    };

    await ApiService.post<User>(
      `${DatabaseEntities.USERS}/activateAccount/${userId}`,
      payload,
    )
      .then(() => {
        AlertService.success(ToastMessages.ACCOUNT_ACTIVATED);
        window.location.href = Pathnames.LOGIN;
      })
      .catch((error) => {
        if (
          error.response &&
          error.response.data === "Account is already activated"
        ) {
          AlertService.error(ToastMessages.ACCOUNT_ALREADY_ACTIVATED);
          window.location.href = Pathnames.LOGIN;
        } else {
          setErrortext(error.response.data);
          setToggleError(true);
        }
      });

    setLoading(false);
  };

  useEffect(() => {
    const keyDownHandler = (event: {
      key: string;
      preventDefault: () => void;
    }) => {
      if (event.key === "Enter") {
        event.preventDefault();
        submit();
      }
    };

    document.addEventListener("keydown", keyDownHandler);

    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  });

  const handleResendCode = async () => {
    if (!toggleResend || !userId || !email) return;

    setLoading(true);

    const user = await ApiService.post<User>(
      `${DatabaseEntities.USERS}/resendActivationCode/${userId}`,
      { email: email },
    ).catch(() => {
      AlertService.error(ToastMessages.FAILED_TO_RESEND_ACTIVATION_CODE);
    });

    if (user != null) {
      await EmailService.sendEmail(
        user.firstName,
        email,
        "Activation Code",
        undefined,
        undefined,
        undefined,
        undefined,
        user,
      ).then(() => {
        AlertService.success(ToastMessages.EMAIL_SENT);
        setToggleResend(false);
      });
    }
    setLoading(false);
  };

  return (
    <div className={classes.login}>
      {loading && <Spinner />}
      <Card className={classes.activationCard}>
        <img src={Logo} className={classes.logo} alt="logo" />
        <CardContent className={classes.container}>
          <div className={classes.activationHeader}>
            <Typography className={classes.boldText}>Set password</Typography>
            <Typography>
              Welcome to setup remodeling. To start of, set your password down
              below
            </Typography>
          </div>
          <div className="form__group">
            <div className={classes.inputDiv}>
              <input
                type={viewNewPassword ? "text" : "password"}
                className={classes.emptyInput}
                placeholder="**********"
                name="new_password"
                id="new_password"
                required
                value={newPassword}
                onChange={(e: any) => setNewPassword(e.target.value)}
              />
              <label className="form__label">New password</label>
              <Button onClick={() => setViewNewPassword(!viewNewPassword)}>
                {viewNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </Button>
            </div>
          </div>
          <div className="form__group">
            <div className={classes.inputDiv}>
              <input
                type={viewConfPassword ? "text" : "password"}
                className={classes.emptyInput}
                placeholder="**********"
                name="password"
                id="password"
                required
                value={confPassword}
                onChange={(e: any) => setConfPassword(e.target.value)}
                onPaste={(e) => e.preventDefault()}
              />
              <label className="form__label">Confirm password</label>
              <Button onClick={() => setViewConfPassword(!viewConfPassword)}>
                {viewConfPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </Button>
            </div>
          </div>
          <div className="form_group mt-4">
            <div className={classes.inputDiv}>
              <input
                className={classes.emptyInput}
                value={activationCode}
                onChange={(e) => setActivationCode(e.target.value)}
                placeholder="Code"
              />
              <button
                onClick={handleResendCode}
                className={classes.resendButton}
                disabled={!toggleResend}
              >
                Resend code
              </button>
            </div>
          </div>
          <div className={classes.passwordCriteria}>
            <Typography>
              New password must meet the following criteria:
            </Typography>
            <ul>
              <li>At least 8 characters long</li>
              <li>Contains at least one uppercase letter (e.g. A-Z)</li>
              <li>Contains at least one lowercase letter (e.g. a-z)</li>
              <li>Contains at least one number (e.g. 0-9)</li>
              <li>Contains at least one special character (e.g. !@#$%&*)</li>
            </ul>
          </div>
          {toggleError && (
            <Typography className={classes.errorText}>{errorText}</Typography>
          )}
          <button
            className={classes.button}
            onClick={submit}
            disabled={invalidInput()}
          >
            Send
          </button>
        </CardContent>
      </Card>
      <ToastContainer />
    </div>
  );
};

export default ActivationAccount;
